/**
 * @file Layout.jsx
 * @description Zentrales App-Layout-Framework von ThekenOS.
 *
 * Was das Layout macht:
 * 1. Hängt die Kopfzeile (`Header`) mit dem passenden Seitentitel oben ein.
 * 2. Stellt den Hauptbereich bereit, in den React Router die jeweilige Seite (`<Outlet />`) einbettet.
 * 3. Ermittelt den Seitentitel dynamisch aus der aktuellen Web-Adresse (URL).
 */

import { Outlet, useLocation } from 'react-router-dom';
import './Layout.css';
import Header from './Header';
import { useAuth } from '../context/AuthContext';

/**
 * Haupt-Layout-Container für alle geschützten Seiten.
 * @returns {JSX.Element|null} Gerahmte Layout-Struktur mit Kopfzeile und Inhaltsbereich
 */
export default function Layout() {
  const { user, loading } = useAuth();
  const location = useLocation();

  /**
   * Ermittelt den passenden deutschen Seitentitel anhand des aktuellen URL-Pfades.
   *
   * @param {string} pathname - Aktueller URL-Pfad (z. B. "/todos", "/einkauf")
   * @returns {string} Lesbarer Seitentitel für die Kopfzeile
   */
  const getPageTitle = (pathname) => {
    if (pathname.startsWith('/thekipedia')) return 'Thekipedia';
    if (pathname.startsWith('/admin/templates')) return 'Aufgaben verwalten';
    if (pathname.startsWith('/admin/users')) return 'Benutzerverwaltung';
    if (pathname.startsWith('/admin/settings')) return 'System-Einstellungen';
    switch (pathname) {
      case '/':
      case '/dashboard': return 'Dashboard';
      case '/todos': return 'Daily Todos';
      case '/einkauf': return 'Einkaufsliste';
      case '/feedback': return 'Feedback & Bugs';
      case '/notes': return 'Gesprächsnotizen';
      case '/mail': return 'Thekenmail';
      default: return 'ThekeOS';
    }
  };

  // Während des initialen Session-Ladens kein unvollständiges Layout anzeigen
  if (loading) return null;

  return (
    <div className="app-container">
      {user && <Header title={getPageTitle(location.pathname)} />}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
